// === Admin: Database Migrations Dashboard ===
// Upload SQL dump → Select table → Migrate with UUID
// Superadmin-only

import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  TableContainer,
  TableHead,
  Dialog,
  Tabs,
  Tab,
  Checkbox,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Dataset as DatabaseIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckIcon,
  CheckCircleOutline as CompletedIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Autorenew as MigratingIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useThemeContext, themeOptions } from '../lib/theme-context';
import { createClient } from '@supabase/supabase-js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TableInfo {
  name: string;
  rowCount: number;
}

interface MigrationRecord {
  migration_id: string;
  source_table_name: string;
  destination_table_name: string;
  rows_attempted: number;
  rows_migrated: number;
  rows_failed: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  progress_percent: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

type DestinationTable = { value: string; label: string };

// A company from the new DB — `old_company_id` is the legacy integer used in the
// SQL dump's company_id columns; `id` is the new UUID written to the destination.
interface Company {
  id: string;
  old_company_id: number;
  company_name: string;
}

const API_BASE = 'https://api.click2gro.com.au/api/v1/admin/migrations';

// Per-table status while the full per-company import runs.
type TableRunState = 'pending' | 'running' | 'done' | 'skipped' | 'error';
interface TableRunStatus {
  state: TableRunState;
  message?: string;
  inserted?: number;
  matched?: number; // rows matching the selected company
}

// Source (MySQL dump) table names that differ from the new Supabase table name.
// Used to auto-suggest a destination; the user can still override per table.
const SOURCE_ALIASES: Record<string, string> = {
  stock_take_audits: 'stock_audits',
  stock_take_audit_items: 'stock_audit_items',
  // Admin/global fertilizer reference table — the new schema misspells it as
  // "fertilzers" (no "i"). The dump's table is the singular "fertilzer".
  // NB: there is also an empty, company-scoped "fertilizers" table — do NOT use it.
  fertilzer: 'fertilzers',
  fertilizers: 'fertilzers',
  fertilizer: 'fertilzers',
};

// Tables whose already-migrated rows reference labour by the OLD integer user IDs
// (imported verbatim — the migration only ever rewrote company_id). The "Remap user
// references" tool rewrites these to the new profile UUIDs. Keyed by the dump's source
// table name; `destination` is the live table (same name here). `listCols` are
// comma-separated id lists; `singleCols` are single ids.
const REMAP_TARGETS: Record<string, { destination: string; label: string; listCols: string[]; singleCols: string[] }> = {
  spray_template:         { destination: 'spray_template',         label: 'Spray tasks',      listCols: ['labour_id'], singleCols: ['assigned_user'] },
  planting_template:      { destination: 'planting_template',      label: 'Planting',         listCols: ['labour_id'], singleCols: ['assigned_user'] },
  harvest_template:       { destination: 'harvest_template',       label: 'Harvest',          listCols: ['labour_id'], singleCols: ['assigned_user'] },
  irrigation_template:    { destination: 'irrigation_template',    label: 'Irrigation',       listCols: ['labour_id'], singleCols: ['assigned_user'] },
  general_task:           { destination: 'general_task',           label: 'General tasks',    listCols: ['labour_id'], singleCols: [] },
  pre_ground_works:       { destination: 'pre_ground_works',       label: 'Pre-ground works', listCols: ['labour_id'], singleCols: [] },
  // staff_id was nulled during import; refilled here from the dump's integer staff_id.
  timesheet_hours_record: { destination: 'timesheet_hours_record', label: 'Timesheets',       listCols: [], singleCols: ['staff_id'] },
};

// Child tables carry no company_id — they're scoped to a company via their parent.
// Maps source table → [parent source table, foreign-key column on the child].
const CHILD_PARENT_FK: Record<string, [parentTable: string, fkColumn: string]> = {
  chemical_order_items: ['chemical_orders', 'order_id'],
  fertilizer_order_items: ['fertilizer_orders', 'order_id'],
  chemical_pricing_history: ['chemical_orders', 'order_id'],
  fertilizer_pricing_history: ['fertilizer_orders', 'order_id'],
  spray_plantings: ['spray_template', 'spray_id'],
};

// Dependency order for the sequential run: parents before children so FK
// validation on the API side passes. Lower number = migrated earlier.
const DESTINATION_RANK = (dest: string): number => {
  const early = ['pest_disease_observations_company', 'chemical_orders', 'fertilizer_orders', 'spray_template'];
  const late = ['chemical_order_items', 'fertilizer_order_items', 'chemical_pricing_history', 'fertilizer_pricing_history', 'spray_plantings'];
  const e = early.indexOf(dest);
  if (e !== -1) return e;
  const l = late.indexOf(dest);
  if (l !== -1) return 100 + l;
  return 50;
};

// Common portal table names that can be migration destinations
const PORTAL_TABLES: DestinationTable[] = [
  { value: 'chemical_stock_movements', label: 'chemical_stock_movements' },
  { value: 'fertilizer_stock_movements', label: 'fertilizer_stock_movements' },
  { value: 'pest_disease_observations', label: 'pest_disease_observations' },
  { value: 'general_tasks', label: 'general_tasks' },
  { value: 'irrigation_records', label: 'irrigation_records' },
  { value: 'irrigation_template', label: 'irrigation_template' },
  { value: 'planting_template', label: 'planting_template' },
  { value: 'timesheets', label: 'timesheets' },
  { value: 'audit_records', label: 'audit_records' },
  { value: 'harvest_records', label: 'harvest_records' },
  // block_reports / qa_reports removed — retired to the `archive` schema
  // (20260730_archive_dead_tables.sql). Reports are computed live, never stored.
  { value: 'spray_tasks', label: 'spray_tasks' },
  { value: 'fertilizer_programs', label: 'fertilizer_programs' },
  { value: 'blocks_glasshouses', label: 'blocks_glasshouses' },
  { value: 'blocks_broadacre', label: 'blocks_broadacre' },
  { value: 'blocks_fixed_irrigation', label: 'blocks_fixed_irrigation' },
  { value: 'blocks_fixed_planting', label: 'blocks_fixed_planting' },
  { value: 'blocks_pivot', label: 'blocks_pivot' },
  { value: 'sprinkler_number', label: 'sprinkler_number (← sprinkler_number)' },
  { value: 'stock_audits', label: 'stock_audits (← stock_take_audits)' },
  { value: 'stock_audit_items', label: 'stock_audit_items (← stock_take_audit_items)' },
  { value: 'companies', label: 'companies' },
  { value: 'users', label: 'users' },
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DatabaseMigrationsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { currentTheme, gradientCards } = useThemeContext();
  const activeTheme = themeOptions.find((t) => t.id === currentTheme) ?? themeOptions[0]!;

  const token = session?.access_token;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State management
  const [stage, setStage] = useState<'upload' | 'parsing' | 'select-table' | 'migration-ready' | 'completed'>('upload');
  const [sqlDumpFile, setSqlDumpFile] = useState<File | null>(null);
  const [detectedTables, setDetectedTables] = useState<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<TableInfo | null>(null);
  const [selectedDestinationTable, setSelectedDestinationTable] = useState<string>('');
  const [availableDestinationTables, setAvailableDestinationTables] = useState<DestinationTable[]>(PORTAL_TABLES as DestinationTable[]);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [parsedRowsByTable, setParsedRowsByTable] = useState<Record<string, Array<Record<string, any>>>>({});

  // Per-company import filter
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  // Per-source-table destination overrides + include toggles + run status
  const [destinationByTable, setDestinationByTable] = useState<Record<string, string>>({});
  const [includeByTable, setIncludeByTable] = useState<Record<string, boolean>>({});
  const [tableRunStatus, setTableRunStatus] = useState<Record<string, TableRunStatus>>({});
  const [runningAll, setRunningAll] = useState(false);

  const [migrationHistory, setMigrationHistory] = useState<MigrationRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState<{
    isRunning: boolean;
    percent: number;
    currentRow: number;
    totalRows: number;
    status: string;
  }>({ isRunning: false, percent: 0, currentRow: 0, totalRows: 0, status: '' });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [errorDetailOpen, setErrorDetailOpen] = useState(false);
  const [selectedErrorRecord, setSelectedErrorRecord] = useState<MigrationRecord | null>(null);
  const [clearingFailed, setClearingFailed] = useState(false);
  const [historyTab, setHistoryTab] = useState<'active' | 'completed'>('active');

  // "Remap user references" tool — preview the old-int-ID → new-profile-UUID map
  // (joined by email) before rewriting labour references in the migrated tables.
  type RemapRow = { oldId: string; email: string | null; profileUuid: string | null; profileName: string | null; status: string };
  type RemapCounts = { total: number; matched: number; no_email: number; no_auth_user: number; not_in_company: number };
  const [remapDialogOpen, setRemapDialogOpen] = useState(false);
  const [remapLoading, setRemapLoading] = useState(false);
  const [remapApplying, setRemapApplying] = useState(false);
  const [remapMapping, setRemapMapping] = useState<RemapRow[]>([]);
  const [remapMap, setRemapMap] = useState<Record<string, string>>({});
  const [remapCounts, setRemapCounts] = useState<RemapCounts | null>(null);

  // Active tab = today's records + any non-clean (failed/stuck/silent-failures).
  // Completed tab = clean completions from previous days.
  const filteredHistory = useMemo(() => {
    const now = new Date();
    const isFromToday = (iso: string | null): boolean => {
      if (!iso) return false;
      const d = new Date(iso);
      return d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
    };
    const isCleanCompletion = (r: MigrationRecord): boolean =>
      r.status === 'completed' && r.rows_failed === 0 && !r.error_message;

    if (historyTab === 'completed') {
      return migrationHistory.filter((r) => isCleanCompletion(r) && !isFromToday(r.created_at));
    }
    return migrationHistory.filter((r) => !isCleanCompletion(r) || isFromToday(r.created_at));
  }, [migrationHistory, historyTab]);

  // =========================================================================
  // Memoized Supabase client - created only once
  // =========================================================================

  const supabaseClient = useMemo(() => {
    const supabaseUrl = import.meta.env['VITE_SUPABASE_URL'];
    const supabaseKey = import.meta.env['VITE_SUPABASE_ANON_KEY'];

    if (!supabaseUrl || !supabaseKey) {
      return null;
    }

    return createClient(supabaseUrl, supabaseKey);
  }, []);

  // =========================================================================
  // =========================================================================
  // Effect: Load migration history on mount
  // =========================================================================

  useEffect(() => {
    if (token) {
      void fetchMigrationHistory();
      void fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // =========================================================================
  // Effect: Load available tables from Supabase on component mount
  // =========================================================================

  useEffect(() => {
    const loadTablesFromSupabase = async () => {
      if (!supabaseClient) {
        console.warn('Supabase client not initialized, using hardcoded table list');
        return;
      }

      try {
        // Fetch all table names from information_schema.tables
        const { data, error } = await supabaseClient.rpc('get_table_names', {});

        if (error) {
          console.warn('Could not fetch tables from Supabase:', error.message);
          // Fallback to hardcoded list
          return;
        }

        if (data && Array.isArray(data) && data.length > 0) {
          // Map DB results to dropdown format
          const fetchedTables: DestinationTable[] = data.map((row: any) => ({
            value: row.table_name || row,
            label: row.table_name || row,
          }));

          // Sort: exact matches first, then alphabetical
          const sortedTables = fetchedTables.sort((a, b) => a.label.localeCompare(b.label));
          setAvailableDestinationTables(sortedTables);
        }
      } catch (err) {
        console.error('Error loading tables from Supabase:', err);
        // Silently fall back to hardcoded list
      }
    };

    loadTablesFromSupabase();
  }, [supabaseClient]);

  // =========================================================================
  // Handler: Upload SQL file
  // =========================================================================

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSqlDumpFile(file);
    parseSqlDump(file);
  };

  // =========================================================================
  // Helper: Parse SQL dump to extract table names and row counts
  // =========================================================================

  const parseSqlDump = async (file: File) => {
    setStage('parsing');
    setParsingError(null);

    try {
      const text = await file.text();
      const tableSet = new Set<string>();

      // Find all table names (fast — just names, not data)
      for (const m of text.matchAll(/INSERT\s+INTO\s+[`"]?(\w+)[`"]?\s*\(/gi)) {
        if (m[1]) tableSet.add(m[1]);
      }

      if (tableSet.size === 0) throw new Error('No INSERT statements found in SQL dump');

      // Extract rows + count using position-based parser
      const tables: TableInfo[] = [];
      const rowsByTable: Record<string, Array<Record<string, any>>> = {};

      for (const tableName of tableSet) {
        const rows = extractTableRows(text, tableName);
        rowsByTable[tableName] = rows;
        tables.push({ name: tableName, rowCount: rows.length });
        console.log(`📊 ${tableName}: ${rows.length} rows`);
      }

      tables.sort((a, b) => a.name.localeCompare(b.name));
      setDetectedTables(tables);
      setParsedRowsByTable(rowsByTable);
      setStage('select-table');
    } catch (err) {
      setParsingError((err as Error).message);
      setStage('upload');
      setSnackbar({ open: true, message: `❌ Parse error: ${(err as Error).message}`, severity: 'error' });
    }
  };

  // =========================================================================
  // Helper: Extract actual row data from SQL INSERT statements
  // Position-based parser — no regex on large text, handles any SQL format
  // =========================================================================
  const extractTableRows = (sqlText: string, tableName: string): Array<Record<string, any>> => {
    const rows: Array<Record<string, any>> = [];
    const upper = sqlText.toUpperCase();
    const needle = 'INSERT INTO';
    let search = 0;

    while (search < sqlText.length) {
      const insertPos = upper.indexOf(needle, search);
      if (insertPos === -1) break;

      // Skip whitespace after INSERT INTO
      let p = insertPos + needle.length;
      while (p < sqlText.length && sqlText[p] === ' ') p++;

      // Skip optional quote/backtick
      if (sqlText[p] === '`' || sqlText[p] === '"') p++;

      // Read table name
      const nameStart = p;
      while (p < sqlText.length && /\w/.test(sqlText[p]!)) p++;
      const foundName = sqlText.slice(nameStart, p);

      if (foundName.toLowerCase() !== tableName.toLowerCase()) {
        search = insertPos + needle.length;
        continue;
      }

      // Skip closing quote/backtick
      if (sqlText[p] === '`' || sqlText[p] === '"') p++;

      // Skip to column list opening paren
      while (p < sqlText.length && sqlText[p] !== '(') p++;
      p++; // skip (

      // Read columns until closing )
      const colEnd = sqlText.indexOf(')', p);
      if (colEnd === -1) break;
      const columns = sqlText.slice(p, colEnd)
        .split(',')
        .map(c => c.trim().replace(/^[`"]+|[`"]+$/g, ''))
        .filter(c => c.length > 0);
      p = colEnd + 1;

      // Find VALUES keyword
      const valuesIdx = upper.indexOf('VALUES', p);
      if (valuesIdx === -1) { search = p; continue; }
      p = valuesIdx + 6;

      // Walk each value tuple until semicolon or end
      while (p < sqlText.length) {
        // Skip whitespace and commas between tuples
        while (p < sqlText.length && /[\s,]/.test(sqlText[p]!)) p++;
        if (p >= sqlText.length || sqlText[p] === ';') break;
        if (sqlText[p] !== '(') break;
        p++; // skip opening (

        // Collect tuple content with string/depth awareness
        let content = '';
        let depth = 1;
        let inStr = false;
        let strChar = '';

        while (p < sqlText.length && depth > 0) {
          const ch = sqlText[p]!;
          if (inStr) {
            if (ch === '\\') { content += ch + (sqlText[p + 1] ?? ''); p += 2; continue; }
            if (ch === strChar) inStr = false;
            content += ch;
          } else {
            if (ch === "'" || ch === '"') { inStr = true; strChar = ch; content += ch; }
            else if (ch === '(') { depth++; content += ch; }
            else if (ch === ')') { depth--; if (depth > 0) content += ch; }
            else { content += ch; }
          }
          p++;
        }

        const values = parseValues(content);
        if (values.length === columns.length) {
          const row: Record<string, any> = {};
          columns.forEach((col, i) => { row[col] = values[i]; });
          rows.push(row);
        } else {
          console.warn(`⚠️ ${tableName}: col/val mismatch — ${columns.length} cols, ${values.length} vals`);
        }
      }

      search = p;
    }

    console.log(`✅ ${tableName}: ${rows.length} rows extracted`);
    return rows;
  };

  // =========================================================================
  // Helper: Parse SQL VALUES - handle strings, numbers, NULL
  // =========================================================================
  const parseValues = (content: string): any[] => {
    const values: any[] = [];
    let current = '';
    let inStr = false;
    let strChar = '';
    let depth = 0;

    for (let i = 0; i < content.length; i++) {
      const ch = content[i]!;
      if (inStr) {
        if (ch === '\\') { current += ch + (content[i + 1] ?? ''); i++; continue; }
        if (ch === strChar) inStr = false;
        current += ch;
      } else {
        if (ch === "'" || ch === '"') { inStr = true; strChar = ch; current += ch; }
        else if (ch === '(') { depth++; current += ch; }
        else if (ch === ')') { depth--; current += ch; }
        else if (ch === ',' && depth === 0) {
          values.push(parseValue(current.trim()));
          current = '';
        } else {
          current += ch;
        }
      }
    }
    if (current.trim()) values.push(parseValue(current.trim()));
    return values;
  };

  // =========================================================================
  // Helper: Parse individual SQL value
  // =========================================================================
  const parseValue = (val: string): any => {
    // NULL
    if (val.toUpperCase() === 'NULL') return null;

    // String (single or double quoted)
    if ((val.startsWith("'") && val.endsWith("'")) || (val.startsWith('"') && val.endsWith('"'))) {
      return val.slice(1, -1).replace(/\\'/g, "'").replace(/\\"/g, '"');
    }

    // Number
    if (/^-?\d+(\.\d+)?$/.test(val)) {
      return /\./.test(val) ? parseFloat(val) : parseInt(val, 10);
    }

    // Boolean
    if (val.toLowerCase() === 'true') return true;
    if (val.toLowerCase() === 'false') return false;

    // Default: string
    return val;
  };

  // =========================================================================
  // Helper: Format date to AU format
  // =========================================================================

  const formatAuDate = (dateString: string | null): string => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    return new Intl.DateTimeFormat('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  };

  // =========================================================================
  // Helper: Get status icon
  // =========================================================================

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'in_progress':
        return <MigratingIcon sx={{ color: 'info.main', fontSize: 20 }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      default:
        return <PendingIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
    }
  };

  // =========================================================================
  // Handler: Fetch migration history from database
  // =========================================================================

  const fetchMigrationHistory = async () => {
    if (!token) return;
    setLoadingHistory(true);
    try {
      const response = await fetch(`https://api.click2gro.com.au/api/v1/admin/migrations/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMigrationHistory(data.records || []);
      }
    } catch (err) {
      console.error('Failed to fetch migration history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // =========================================================================
  // Handler: Fetch companies for the per-company import filter
  // =========================================================================

  const fetchCompanies = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies((data.companies as Company[]) || []);
      } else {
        console.warn('Failed to load companies for migration filter:', response.status);
      }
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  // =========================================================================
  // Helpers: per-table destination / include resolution + company row filtering
  // =========================================================================

  // Parsed rows for a detected table, looked up case-insensitively.
  const getParsedRows = (sourceName: string): Array<Record<string, any>> => {
    if (parsedRowsByTable[sourceName]) return parsedRowsByTable[sourceName]!;
    const key = Object.keys(parsedRowsByTable).find((k) => k.toLowerCase() === sourceName.toLowerCase());
    return key ? parsedRowsByTable[key]! : [];
  };

  // Auto-suggested destination for a source table (exact name, then alias map).
  const getDefaultDestination = (sourceName: string): string => {
    const lower = sourceName.toLowerCase();
    const candidate = SOURCE_ALIASES[lower] ?? lower;
    return availableDestinationTables.find((t) => t.value === candidate)?.value ?? '';
  };

  const effectiveDestination = (sourceName: string): string =>
    destinationByTable[sourceName] !== undefined ? destinationByTable[sourceName]! : getDefaultDestination(sourceName);

  // How this table is scoped to a company: directly via company_id, via a parent
  // (child tables), or not at all (global reference data — excluded from per-company runs).
  const companyScopeKind = (sourceName: string): 'direct' | 'child' | 'none' => {
    if (CHILD_PARENT_FK[sourceName.toLowerCase()]) return 'child';
    if (getParsedRows(sourceName).some((r) => 'company_id' in r)) return 'direct';
    return 'none';
  };

  const effectiveInclude = (sourceName: string): boolean => {
    if (includeByTable[sourceName] !== undefined) return includeByTable[sourceName]!;
    // Default on when we have a destination AND the table can be scoped to a company.
    return !!effectiveDestination(sourceName) && companyScopeKind(sourceName) !== 'none';
  };

  // Filter a table's parsed rows down to a single company (by legacy integer id).
  const filterRowsForCompany = (sourceName: string, rows: Array<Record<string, any>>, oldCompanyId: number): Array<Record<string, any>> => {
    const kind = companyScopeKind(sourceName);
    if (kind === 'direct') {
      return rows.filter((r) => 'company_id' in r && r['company_id'] != null && Number(r['company_id']) === oldCompanyId);
    }
    if (kind === 'child') {
      const [parentTable, fk] = CHILD_PARENT_FK[sourceName.toLowerCase()]!;
      const parentRows = getParsedRows(parentTable);
      const parentIds = new Set(
        parentRows
          .filter((p) => 'company_id' in p && Number(p['company_id']) === oldCompanyId)
          .map((p) => Number(p['id'])),
      );
      return rows.filter((r) => fk in r && r[fk] != null && parentIds.has(Number(r[fk])));
    }
    return [];
  };

  // =========================================================================
  // Handler: Import every matched table for the selected company (sequential)
  // =========================================================================

  const handleMigrateAllForCompany = async () => {
    if (!token) {
      setSnackbar({ open: true, message: '❌ Missing auth token', severity: 'error' });
      return;
    }

    // Company is required only when a company-scoped table is included. Admin/
    // global tables (no company_id, e.g. `chemical`) import all rows and need
    // no company selection.
    const includedScoped = detectedTables.some(
      (t) => effectiveInclude(t.name) && effectiveDestination(t.name) && companyScopeKind(t.name) !== 'none',
    );
    if (includedScoped && !selectedCompany) {
      setSnackbar({ open: true, message: '❌ Select a company first (company-scoped tables are included)', severity: 'error' });
      return;
    }

    setRunningAll(true);

    // Build the work list — included tables with a destination — in dependency order.
    const work = detectedTables
      .filter((t) => effectiveInclude(t.name) && effectiveDestination(t.name))
      .sort((a, b) => DESTINATION_RANK(effectiveDestination(a.name)) - DESTINATION_RANK(effectiveDestination(b.name)));

    // Seed statuses: queued tables pending, everything else marked skipped with a reason.
    const initStatus: Record<string, TableRunStatus> = {};
    const workNames = new Set(work.map((w) => w.name));
    for (const t of detectedTables) {
      initStatus[t.name] = workNames.has(t.name)
        ? { state: 'pending' }
        : { state: 'skipped', message: effectiveDestination(t.name) ? 'excluded' : 'no destination match' };
    }
    setTableRunStatus(initStatus);

    let totalInserted = 0;

    for (const t of work) {
      const dest = effectiveDestination(t.name);
      const isGlobal = companyScopeKind(t.name) === 'none';
      // Global/admin tables import every row; company tables are filtered.
      const filtered = isGlobal
        ? getParsedRows(t.name)
        : filterRowsForCompany(t.name, getParsedRows(t.name), selectedCompany!.old_company_id);

      setTableRunStatus((prev) => ({ ...prev, [t.name]: { state: 'running', matched: filtered.length } }));

      if (filtered.length === 0) {
        setTableRunStatus((prev) => ({ ...prev, [t.name]: { state: 'skipped', message: isGlobal ? '0 rows in dump' : '0 rows for this company', matched: 0 } }));
        continue;
      }

      try {
        // Create the migration record for history.
        const startRes = await fetch(`${API_BASE}/start`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ sourceTable: t.name, destinationTable: dest }),
        });
        if (!startRes.ok) {
          const e = await startRes.json().catch(() => ({}));
          throw new Error(e.error || `start failed (${startRes.status})`);
        }
        const { migration_id } = await startRes.json();

        // Execute with the per-company filter (scoped-replace on the API side).
        const execRes = await fetch(`${API_BASE}/execute`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            migration_id,
            destination_table: dest,
            rows: filtered,
            // Company-scoped table → send the filter (scoped replace, other
            // companies untouched). Admin/global table → no filter, so the
            // backend truncates + imports the whole table.
            ...(isGlobal ? {} : {
              filter_old_company_id: selectedCompany!.old_company_id,
              filter_company_uuid: selectedCompany!.id,
            }),
          }),
        });
        const execData = await execRes.json();
        if (!execRes.ok) throw new Error(execData.error || `execute failed (${execRes.status})`);

        totalInserted += execData.inserted ?? 0;
        const failed = execData.failed ?? 0;
        setTableRunStatus((prev) => ({
          ...prev,
          [t.name]: {
            state: failed > 0 ? 'error' : 'done',
            inserted: execData.inserted ?? 0,
            matched: filtered.length,
            ...(failed > 0 ? { message: `${failed} failed` } : {}),
          },
        }));
      } catch (err) {
        setTableRunStatus((prev) => ({
          ...prev,
          [t.name]: { state: 'error', message: err instanceof Error ? err.message : String(err), matched: filtered.length },
        }));
      }
    }

    setRunningAll(false);
    await fetchMigrationHistory();
    setSnackbar({
      open: true,
      message: `✅ Import complete${selectedCompany ? ` for ${selectedCompany.company_name}` : ''}: ${totalInserted} rows inserted`,
      severity: 'success',
    });
  };

  // =========================================================================
  // Handler: Build the old-int-ID → new-profile-UUID map (preview, no writes)
  // Uses the dump's `users` table (id + email) joined by email on the server.
  // =========================================================================

  const handleBuildUserMap = async () => {
    if (!token) {
      setSnackbar({ open: true, message: '❌ Missing auth token', severity: 'error' });
      return;
    }
    if (!selectedCompany) {
      setSnackbar({ open: true, message: '❌ Select a company first — the map is company-scoped', severity: 'error' });
      return;
    }
    const userRows = getParsedRows('users');
    if (userRows.length === 0) {
      setSnackbar({ open: true, message: '❌ No `users` table found in this SQL dump', severity: 'error' });
      return;
    }

    setRemapLoading(true);
    try {
      // Email is globally unique, and the server only matches profiles in the selected
      // company, so we can send every dump user (no company pre-filter needed here).
      const users = userRows.map((r) => ({ id: r['id'], email: r['email'], first_name: r['first_name'], last_name: r['last_name'] }));
      const res = await fetch(`${API_BASE}/build-user-map`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ users, filter_company_uuid: selectedCompany.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `build-user-map failed (${res.status})`);

      setRemapMapping(data.mapping ?? []);
      setRemapMap(data.map ?? {});
      setRemapCounts(data.counts ?? null);
      setRemapDialogOpen(true);
    } catch (err) {
      setSnackbar({ open: true, message: `❌ ${err instanceof Error ? err.message : String(err)}`, severity: 'error' });
    } finally {
      setRemapLoading(false);
    }
  };

  // =========================================================================
  // Handler: Apply the remap — rewrite labour references in migrated rows.
  // In-place UPDATEs keyed by id; only the user-ref columns are touched. Rows are
  // chunked to stay under the Cloudflare Workers subrequest limit.
  // =========================================================================

  const handleApplyRemap = async () => {
    if (!token || !selectedCompany) return;
    setRemapApplying(true);

    const CHUNK = 40;
    let totalUpdated = 0;
    let totalSkipped = 0;
    let totalFailed = 0;
    const tableSummaries: string[] = [];

    try {
      for (const [source, cfg] of Object.entries(REMAP_TARGETS)) {
        const allRows = getParsedRows(source);
        if (allRows.length === 0) continue; // table not in this dump

        const companyRows = filterRowsForCompany(source, allRows, selectedCompany.old_company_id);
        if (companyRows.length === 0) continue;

        const refCols = [...cfg.listCols, ...cfg.singleCols];
        // Project to id + the ref columns to keep the payload small.
        const payloadRows = companyRows.map((r) => {
          const o: Record<string, any> = { id: r['id'] };
          for (const col of refCols) o[col] = r[col];
          return o;
        });

        let tableUpdated = 0;
        let tableFailed = 0;
        for (let i = 0; i < payloadRows.length; i += CHUNK) {
          const chunk = payloadRows.slice(i, i + CHUNK);
          const res = await fetch(`${API_BASE}/remap-users`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              map: remapMap,
              filter_company_uuid: selectedCompany.id,
              destination_table: cfg.destination,
              listCols: cfg.listCols,
              singleCols: cfg.singleCols,
              rows: chunk,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(`${cfg.destination}: ${data.error || res.status}`);
          tableUpdated += data.updated ?? 0;
          totalUpdated += data.updated ?? 0;
          totalSkipped += data.skipped ?? 0;
          tableFailed += data.failed ?? 0;
          totalFailed += data.failed ?? 0;
        }
        tableSummaries.push(`${cfg.destination}: ${tableUpdated} updated${tableFailed > 0 ? `, ${tableFailed} failed` : ''}`);
      }

      setRemapDialogOpen(false);
      setSnackbar({
        open: true,
        message: `✅ Remap complete for ${selectedCompany.company_name}: ${totalUpdated} updated, ${totalSkipped} unchanged${totalFailed > 0 ? `, ${totalFailed} failed` : ''}`,
        severity: totalFailed > 0 ? 'error' : 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: `❌ Remap failed — ${err instanceof Error ? err.message : String(err)}`, severity: 'error' });
    } finally {
      setRemapApplying(false);
    }
  };

  // =========================================================================
  // Per-section impact for the preview: how many rows in each table reference a
  // matched person, so the admin can see exactly WHERE the conversion applies
  // before agreeing. Computed client-side from the parsed dump + matched map.
  // =========================================================================

  const getRemapImpact = (): Array<{ section: string; rows: number; people: Array<{ name: string; count: number }> }> => {
    if (!selectedCompany) return [];
    const matchedIds = new Set(Object.keys(remapMap));
    if (matchedIds.size === 0) return [];
    const nameById = new Map(
      remapMapping.filter((m) => m.status === 'matched').map((m) => [m.oldId, m.profileName || m.oldId] as const),
    );

    const out: Array<{ section: string; rows: number; people: Array<{ name: string; count: number }> }> = [];
    for (const [source, cfg] of Object.entries(REMAP_TARGETS)) {
      const all = getParsedRows(source);
      if (all.length === 0) continue; // table not in this dump
      const companyRows = filterRowsForCompany(source, all, selectedCompany.old_company_id);
      const refCols = [...cfg.listCols, ...cfg.singleCols];
      const perPerson = new Map<string, number>();
      let rows = 0;
      for (const r of companyRows) {
        let hit = false;
        for (const col of refCols) {
          const raw = r[col];
          if (raw == null) continue;
          for (const t of String(raw).split(',').map((s) => s.trim()).filter(Boolean)) {
            if (matchedIds.has(t)) { hit = true; perPerson.set(t, (perPerson.get(t) ?? 0) + 1); }
          }
        }
        if (hit) rows++;
      }
      if (rows > 0) {
        out.push({
          section: cfg.label,
          rows,
          people: [...perPerson.entries()]
            .map(([oldId, count]) => ({ name: nameById.get(oldId) ?? oldId, count }))
            .sort((a, b) => b.count - a.count),
        });
      }
    }
    return out;
  };

  // =========================================================================
  // Handler: Show error details in modal
  // =========================================================================

  const handleShowError = (record: MigrationRecord) => {
    setSelectedErrorRecord(record);
    setErrorDetailOpen(true);
  };

  // =========================================================================
  // Handler: Clear failed migrations from the table
  // =========================================================================

  const handleClearFailed = async () => {
    setClearingFailed(true);
    try {
      // Call API to soft-delete failed migrations
      const response = await fetch('https://api.click2gro.com.au/api/v1/admin/migrations/clear-failed', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        let errorMessage = `API returned ${response.status}`;
        try {
          const errorData = await response.json() as { error?: string };
          errorMessage = errorData.error || errorMessage;
        } catch {
          // If response is not JSON (e.g., HTML error page), use status message
          errorMessage = `${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Refresh the migration history from database
      await fetchMigrationHistory();

      setSnackbar({
        open: true,
        message: '✅ Failed migrations cleared',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: `❌ Failed to clear: ${err instanceof Error ? err.message : String(err)}`,
        severity: 'error',
      });
    } finally {
      setClearingFailed(false);
    }
  };

  // =========================================================================
  // Render: Upload Stage
  // =========================================================================

  if (stage === 'upload') {
    return (
      <>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
            <Tooltip title="Back to Dashboard">
              <IconButton onClick={() => navigate('/dashboard')} size="small">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Database Migrations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Import SQL dumps and migrate tables with UUID company IDs
              </Typography>
            </Box>
          </Box>
        </Container>

        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              background: gradientCards ? activeTheme.gradient : 'background.paper',
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />

            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Database Migration
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              Upload Click2Gro30 SQL dump to migrate tables with UUID company IDs
            </Typography>

            <Button
              variant="contained"
              sx={{ px: 4, py: 1.5 }}
              onClick={() => fileInputRef.current?.click()}
            >
              Select SQL Dump File
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".sql"
              onChange={handleFileSelect}
            />

            {sqlDumpFile && (
              <Box sx={{ mt: 3 }}>
                <Chip
                  icon={<CheckIcon />}
                  label={sqlDumpFile.name}
                  color="success"
                  variant="outlined"
                />
              </Box>
            )}

            {parsingError && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {parsingError}
              </Alert>
            )}
          </Paper>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            message={snackbar.message}
          />
        </Container>

        {/* Migration History - always visible */}
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Migration History
            </Typography>
            {migrationHistory.some((r) => r.status !== 'completed' || r.rows_failed > 0 || r.error_message) && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleClearFailed}
                disabled={clearingFailed}
              >
                {clearingFailed ? 'Clearing...' : 'Clear Failed/Stuck'}
              </Button>
            )}
          </Box>

          <Tabs
            value={historyTab}
            onChange={(_, v) => setHistoryTab(v as 'active' | 'completed')}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Active" value="active" />
            <Tab label="Completed" value="completed" />
          </Tabs>

          {loadingHistory ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Paper>
          ) : filteredHistory.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {migrationHistory.length === 0
                  ? 'No migrations recorded yet'
                  : historyTab === 'completed'
                    ? 'No completed migrations from previous days'
                    : 'No active migrations to display'}
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source Table</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Destination Table</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Rows
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Migrated Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistory.map((record, idx) => (
                    <TableRow key={record.migration_id} sx={{ bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}>
                      <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon(record.status)}
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {record.status}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {record.source_table_name}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {record.destination_table_name}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${record.rows_migrated}/${record.rows_attempted}`}
                          size="small"
                          variant="outlined"
                          color={record.rows_failed === 0 ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>{formatAuDate(record.completed_at)}</TableCell>
                      <TableCell>{formatAuDate(record.created_at)}</TableCell>
                      <TableCell>
                        {record.error_message ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleShowError(record)}
                          >
                            View Error
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Container>

        {/* Error Detail Dialog */}
        <Dialog
          open={errorDetailOpen}
          onClose={() => setErrorDetailOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Migration Error Details
            </Typography>
            {selectedErrorRecord && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  <strong>Migration ID:</strong> {selectedErrorRecord.migration_id}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  <strong>Source:</strong> {selectedErrorRecord.source_table_name} → {selectedErrorRecord.destination_table_name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, fontWeight: 600 }}>
                  Error Message:
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: 'action.hover',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '300px',
                    overflow: 'auto',
                  }}
                >
                  {selectedErrorRecord.error_message}
                </Paper>
              </Box>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setErrorDetailOpen(false)}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Dialog>
      </>
    );
  }

  // =========================================================================
  // Render: Parsing Stage
  // =========================================================================

  if (stage === 'parsing') {
    return (
      <>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
            <Tooltip title="Back to Dashboard">
              <IconButton onClick={() => navigate('/dashboard')} size="small">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Database Migrations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Import SQL dumps and migrate tables with UUID company IDs
              </Typography>
            </Box>
          </Box>
        </Container>

        <Container maxWidth="sm" sx={{ py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <Paper sx={{ p: 6, textAlign: 'center', width: '100%', bgcolor: gradientCards ? undefined : 'background.paper' }}>
            <Box sx={{ mb: 3 }}>
              <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Parsing SQL Dump...
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
              Detecting tables and row counts
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', animation: 'pulse 1.5s ease-in-out infinite' }} />
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', animation: 'pulse 1.5s ease-in-out infinite 0.3s' }} />
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', animation: 'pulse 1.5s ease-in-out infinite 0.6s' }} />
            </Box>
            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 0.3; }
                50% { opacity: 1; }
              }
            `}</style>
          </Paper>
        </Container>
      </>
    );
  }

  // =========================================================================
  // Render: Table Selection Stage (with side-by-side migration config)
  // =========================================================================

  if (stage === 'select-table') {
    // Admin/global tables (no company_id) can be imported without a company.
    const anyGlobalIncluded = detectedTables.some(
      (t) => effectiveInclude(t.name) && effectiveDestination(t.name) && companyScopeKind(t.name) === 'none',
    );
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
            <Tooltip title="Back to Dashboard">
              <IconButton onClick={() => navigate('/dashboard')} size="small">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Database Migrations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Import SQL dumps and migrate tables with UUID company IDs
              </Typography>
            </Box>
          </Box>

          {/* Company selector + run control */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: gradientCards ? activeTheme.gradient : 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <DatabaseIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {detectedTables.length} tables detected
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {sqlDumpFile?.name}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Pick a company, then import every matched table for that company — one table at a time.
              Only the selected company&apos;s rows are imported, and only that company&apos;s existing
              rows are replaced (other companies&apos; data is left untouched).
              Admin/global tables (no company column, e.g. <strong>chemical</strong>) can be ticked and
              imported without a company — these <strong>replace the entire table</strong>.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Autocomplete
                options={companies}
                getOptionLabel={(o) => `${o.company_name} (#${o.old_company_id})`}
                value={selectedCompany}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(_, v) => setSelectedCompany(v)}
                disabled={runningAll}
                sx={{ minWidth: 360, flex: 1 }}
                renderInput={(params) => (
                  // @ts-expect-error: MUI Autocomplete renderInput params have optional props incompatible with exactOptionalPropertyTypes
                  <TextField {...params} size="small" label="Company to import" placeholder="Search by name…" />
                )}
              />
              <Button
                variant="contained"
                onClick={handleMigrateAllForCompany}
                disabled={runningAll || (!selectedCompany && !anyGlobalIncluded)}
                startIcon={runningAll ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
              >
                {runningAll ? 'Importing…' : selectedCompany ? 'Import all tables for company' : 'Import admin tables'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleBuildUserMap}
                disabled={runningAll || remapLoading || !selectedCompany || getParsedRows('users').length === 0}
                startIcon={remapLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {remapLoading ? 'Building map…' : 'Remap user references'}
              </Button>
              <Button
                variant="text"
                startIcon={<ArrowBackIcon />}
                disabled={runningAll}
                onClick={() => {
                  setStage('upload');
                  setSqlDumpFile(null);
                  setDetectedTables([]);
                  setTableRunStatus({});
                }}
              >
                Upload Different File
              </Button>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              <strong>Remap user references</strong> rewrites old labour IDs (labour_id / assigned_user / staff_id)
              in already-migrated rows to the new Staff Management profile UUIDs — matched by email. It updates rows
              in place (no re-import) and shows a preview before writing.
            </Typography>

            {companies.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No companies loaded. Make sure the <strong>companies</strong> table has been migrated first
                (the import matches the dump&apos;s integer company_id to each company&apos;s old_company_id).
              </Alert>
            )}
          </Paper>

          {/* Table → destination mapping */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 600, width: '1%', whiteSpace: 'nowrap' }}>Source Table</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap', pr: 5 }}>Rows for company</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Destination Table</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detectedTables.map((table) => {
                  const dest = effectiveDestination(table.name);
                  const scope = companyScopeKind(table.name);
                  const include = effectiveInclude(table.name);
                  const matching = scope === 'none'
                    ? getParsedRows(table.name).length // admin/global: all rows, no company filter
                    : selectedCompany
                      ? filterRowsForCompany(table.name, getParsedRows(table.name), selectedCompany.old_company_id).length
                      : null;
                  const status = tableRunStatus[table.name];
                  const statusColor =
                    status?.state === 'done' ? 'success.main'
                    : status?.state === 'error' ? 'error.main'
                    : status?.state === 'running' ? 'info.main'
                    : 'text.secondary';
                  const statusLabel =
                    status?.state === 'done' ? `✓ ${status.inserted ?? 0} inserted`
                    : status?.state === 'error' ? `✗ ${status.message ?? 'error'}`
                    : status?.state === 'running' ? 'Running…'
                    : status?.state === 'skipped' ? `Skipped (${status.message ?? ''})`
                    : '—';
                  return (
                    <TableRow key={table.name} sx={{ opacity: include ? 1 : 0.5 }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={include}
                          disabled={runningAll || !dest}
                          onChange={(e) => setIncludeByTable((prev) => ({ ...prev, [table.name]: e.target.checked }))}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', width: '1%', whiteSpace: 'nowrap' }}>
                        {table.name}
                        {scope === 'none' && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                            admin/global table — imports all rows (replaces table)
                          </Typography>
                        )}
                        {scope === 'child' && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            scoped via {CHILD_PARENT_FK[table.name.toLowerCase()]![0]}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 5 }}>
                        {matching === null ? (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>select a company</Typography>
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={matching > 0 ? 'primary' : 'default'}
                            label={matching}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={availableDestinationTables}
                          getOptionLabel={(o) => o.label}
                          value={availableDestinationTables.find((t) => t.value === dest) ?? null}
                          isOptionEqualToValue={(o, v) => o.value === v.value}
                          onChange={(_, v) => setDestinationByTable((prev) => ({ ...prev, [table.name]: v?.value ?? '' }))}
                          disabled={runningAll}
                          sx={{ minWidth: 260 }}
                          renderInput={(params) => (
                            // @ts-expect-error: MUI Autocomplete renderInput params have optional props incompatible with exactOptionalPropertyTypes
                            <TextField {...params} size="small" placeholder="(skip — no match)" />
                          )}
                        />
                      </TableCell>
                      <TableCell sx={{ color: statusColor, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {statusLabel}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            message={snackbar.message}
          />

          {/* Remap user references — preview & confirm */}
          <Dialog open={remapDialogOpen} onClose={() => !remapApplying && setRemapDialogOpen(false)} maxWidth="md" fullWidth>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Remap user references — preview
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Old user IDs are matched to new Staff Management profiles by email for{' '}
                <strong>{selectedCompany?.company_name}</strong>. Only <strong>matched</strong> people will be
                rewritten; unmatched IDs are left untouched. This updates rows in place — no re-import.
              </Typography>

              {remapCounts && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Chip size="small" color="success" label={`${remapCounts.matched} matched`} />
                  <Chip size="small" color="default" label={`${remapCounts.no_email} no email`} />
                  <Chip size="small" color="warning" variant="outlined" label={`${remapCounts.no_auth_user} no account`} />
                  <Chip size="small" color="warning" variant="outlined" label={`${remapCounts.not_in_company} not in company`} />
                  <Chip size="small" variant="outlined" label={`${remapCounts.total} total`} />
                </Box>
              )}

              {/* Where this will apply — per-section impact so the admin can agree to the scope */}
              {remapDialogOpen && (() => {
                const impact = getRemapImpact();
                const totalRows = impact.reduce((s, i) => s + i.rows, 0);
                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Where this will apply — {totalRows} row{totalRows === 1 ? '' : 's'} across {impact.length} section{impact.length === 1 ? '' : 's'}
                    </Typography>
                    {impact.length === 0 ? (
                      <Alert severity="info" sx={{ py: 0 }}>
                        No {selectedCompany?.company_name} rows reference the matched people — nothing would change.
                      </Alert>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Rows to update</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>People (rows)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {impact.map((i) => (
                              <TableRow key={i.section}>
                                <TableCell sx={{ fontWeight: 600 }}>{i.section}</TableCell>
                                <TableCell align="right">
                                  <Chip size="small" color="primary" variant="outlined" label={i.rows} />
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.78rem' }}>
                                  {i.people.map((p) => `${p.name} (${p.count})`).join(', ')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                );
              })()}

              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                Full match list (matched rows above are what gets converted):
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 280 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Old ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Matched profile</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {remapMapping.map((m) => (
                      <TableRow key={m.oldId} sx={{ opacity: m.status === 'matched' ? 1 : 0.6 }}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{m.oldId}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{m.email ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{m.profileName || (m.profileUuid ?? '—')}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant={m.status === 'matched' ? 'filled' : 'outlined'}
                            color={m.status === 'matched' ? 'success' : 'default'}
                            label={m.status}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button variant="outlined" onClick={() => setRemapDialogOpen(false)} disabled={remapApplying}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApplyRemap}
                  disabled={remapApplying || !remapCounts || remapCounts.matched === 0}
                  startIcon={remapApplying ? <CircularProgress size={18} color="inherit" /> : undefined}
                >
                  {remapApplying ? 'Applying…' : `Apply remap (${remapCounts?.matched ?? 0})`}
                </Button>
              </Box>
            </Box>
          </Dialog>
        </Container>
      </Box>
    );
  }

  // =========================================================================
  // Render: Migration Completed Stage
  // =========================================================================

  if (stage === 'completed') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setStage('upload');
                setSqlDumpFile(null);
                setDetectedTables([]);
                setSelectedTable(null);
                setSelectedDestinationTable('');
                setMigrationProgress({ isRunning: false, percent: 0, currentRow: 0, totalRows: 0, status: '' });
              }}
              variant="text"
              sx={{ textTransform: 'none' }}
            >
              Back to Migrations
            </Button>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Database Migrations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Import SQL dumps and migrate tables with UUID company IDs
              </Typography>
            </Box>
          </Box>
        </Container>

        <Container maxWidth="md" sx={{ py: 4 }}>
          {/* Success Card */}
          <Paper sx={{ p: 4, textAlign: 'center', mb: 4, border: '2px solid', borderColor: 'success.main' }}>
            <CompletedIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Migration Completed Successfully
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {selectedTable?.rowCount} rows migrated to {selectedDestinationTable}
            </Typography>
            {migrationProgress.currentRow > 0 && (
              <Chip
                label={`${migrationProgress.currentRow} / ${migrationProgress.totalRows} rows`}
                color="success"
                variant="outlined"
              />
            )}
          </Paper>

          {/* Next Steps */}
          <Alert severity="success" sx={{ mb: 4, bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.dark' }}>
              ✅ Migration Completed — Automatic Sequence Reset Applied
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 2, fontSize: '0.9rem', color: 'text.primary' }}>
              <li>Data has been saved to <strong>{selectedDestinationTable}</strong></li>
              <li>Company IDs automatically converted from integers to UUIDs</li>
              <li><strong>✅ All SERIAL sequences automatically reset</strong> — prevents duplicate key errors</li>
              <li>Migration record created with full audit trail</li>
              <li>View the history below to track all migrations</li>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary', fontStyle: 'italic' }}>
              No manual sequence reset needed. Next INSERT operations will auto-increment correctly.
            </Typography>
          </Alert>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => {
                setStage('upload');
                setSqlDumpFile(null);
                setDetectedTables([]);
                setSelectedTable(null);
                setSelectedDestinationTable('');
                setMigrationProgress({ isRunning: false, percent: 0, currentRow: 0, totalRows: 0, status: '' });
              }}
            >
              Start New Migration
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </Box>
        </Container>

        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Migration History
            </Typography>
            {migrationHistory.some((r) => r.status !== 'completed' || r.rows_failed > 0 || r.error_message) && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleClearFailed}
                disabled={clearingFailed}
              >
                {clearingFailed ? 'Clearing...' : 'Clear Failed/Stuck'}
              </Button>
            )}
          </Box>

          <Tabs
            value={historyTab}
            onChange={(_, v) => setHistoryTab(v as 'active' | 'completed')}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Active" value="active" />
            <Tab label="Completed" value="completed" />
          </Tabs>

          {loadingHistory ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Paper>
          ) : filteredHistory.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {migrationHistory.length === 0
                  ? 'No migrations recorded yet'
                  : historyTab === 'completed'
                    ? 'No completed migrations from previous days'
                    : 'No active migrations to display'}
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source Table</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Destination Table</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Rows
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Migrated Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistory.map((record, idx) => (
                    <TableRow key={record.migration_id} sx={{ bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}>
                      <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon(record.status)}
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {record.status}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {record.source_table_name}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {record.destination_table_name}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${record.rows_migrated}/${record.rows_attempted}`}
                          size="small"
                          variant="outlined"
                          color={record.rows_failed === 0 ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>{formatAuDate(record.completed_at)}</TableCell>
                      <TableCell>{formatAuDate(record.created_at)}</TableCell>
                      <TableCell>
                        {record.error_message ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleShowError(record)}
                          >
                            View Error
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Container>

        {/* Error Detail Dialog */}
        <Dialog
          open={errorDetailOpen}
          onClose={() => setErrorDetailOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Migration Error Details
            </Typography>
            {selectedErrorRecord && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  <strong>Migration ID:</strong> {selectedErrorRecord.migration_id}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  <strong>Source:</strong> {selectedErrorRecord.source_table_name} → {selectedErrorRecord.destination_table_name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, fontWeight: 600 }}>
                  Error Message:
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: 'action.hover',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '300px',
                    overflow: 'auto',
                  }}
                >
                  {selectedErrorRecord.error_message}
                </Paper>
              </Box>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setErrorDetailOpen(false)}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    );
  }

  // Fallback
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              Database Migrations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Import SQL dumps and migrate tables with UUID company IDs
            </Typography>
          </Box>
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Migration History
        </Typography>

        {loadingHistory ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Paper>
        ) : migrationHistory.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No migrations recorded yet</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Source Table</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Destination Table</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Rows
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Migrated Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {migrationHistory.map((record, idx) => (
                  <TableRow key={record.migration_id} sx={{ bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}>
                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(record.status)}
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {record.status}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {record.source_table_name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {record.destination_table_name}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${record.rows_migrated}/${record.rows_attempted}`}
                        size="small"
                        variant="outlined"
                        color={record.rows_failed === 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{formatAuDate(record.completed_at)}</TableCell>
                    <TableCell>{formatAuDate(record.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
}

